import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, IconButton, Button, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, TableSortLabel
} from '@mui/material';
import { Edit as EditIcon, LockReset as LockResetIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import api from '../../utils/axios';
import { toast } from 'react-toastify';
import { getComparator, stableSort } from '../../utils/sortUtils';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Sorting State
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('firstName');

  // Edit Form State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [isPro, setIsPro] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/superadmin/users');
      if (res.data.success) {
        setUsers(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setEmail(user.email || '');
    setRole(user.role);
    setIsPro(user.isPro || false);
    setNewPassword('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = { firstName, lastName, email, role, isPro };
      if (newPassword) payload.newPassword = newPassword;
      
      const res = await api.put(`/superadmin/users/${selectedUser._id}`, payload);
      if (res.data.success) {
        toast.success('User updated successfully');
        fetchUsers();
        setOpenDialog(false);
      }
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleSendResetEmail = async (userId) => {
    if (window.confirm('Are you sure you want to send a random password reset to this user?')) {
      try {
        const res = await api.post(`/superadmin/users/${userId}/reset-password`);
        if (res.data.success) {
          toast.success('Reset email sent!');
        }
      } catch (err) {
        toast.error('Failed to send reset email');
      }
    }
  };

  const getRoleColor = (r) => {
    switch(r) {
      case 'superadmin': return 'error';
      case 'admin': return 'secondary';
      case 'leader': return 'info';
      default: return 'default';
    }
  };

  const sortedUsers = stableSort(users, getComparator(order, orderBy));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Manage Users</Typography>
      
      <Card sx={{ mt: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'firstName'}
                    direction={orderBy === 'firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('firstName')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'church.name'}
                    direction={orderBy === 'church.name' ? order : 'asc'}
                    onClick={() => handleRequestSort('church.name')}
                  >
                    Church
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'role'}
                    direction={orderBy === 'role' ? order : 'asc'}
                    onClick={() => handleRequestSort('role')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'isPro'}
                    direction={orderBy === 'isPro' ? order : 'asc'}
                    onClick={() => handleRequestSort('isPro')}
                  >
                    PRO
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>{user.firstName} {user.lastName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.church?.name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip label={user.role} color={getRoleColor(user.role)} size="small" />
                  </TableCell>
                  <TableCell>
                    {user.isPro ? <CheckCircleIcon color="success" /> : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleSendResetEmail(user._id)} title="Send Reset Email" color="warning">
                      <LockResetIcon />
                    </IconButton>
                    <IconButton onClick={() => handleEditClick(user)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center">No users found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {selectedUser?.firstName}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                fullWidth 
                label="First Name" 
                value={firstName} 
                onChange={(e) => setFirstName(e.target.value)} 
              />
              <TextField 
                fullWidth 
                label="Last Name" 
                value={lastName} 
                onChange={(e) => setLastName(e.target.value)} 
              />
            </Box>

            <TextField 
              fullWidth 
              label="Email Address" 
              type="email"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="musician">Musician</MenuItem>
                <MenuItem value="leader">Leader</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="superadmin">Superadmin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>PRO Status</InputLabel>
              <Select value={isPro} label="PRO Status" onChange={(e) => setIsPro(e.target.value)}>
                <MenuItem value={true}>Active PRO</MenuItem>
                <MenuItem value={false}>No PRO</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #eee' }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Manual Password Overwrite
              </Typography>
              <TextField 
                fullWidth 
                size="small" 
                placeholder="Leave blank to keep current password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageUsers;
